import { useId, useState } from 'react';

import Button from '../../../common/components/button/button';
import IconButton from '../../../common/components/icon-button/icon-button';
import PickerSheet from '../../../common/components/picker-sheet/picker-sheet';
import Sheet from '../../../common/components/sheet/sheet';
import Toggle from '../../../common/components/toggle/toggle';
import FilterIcon from '../../../common/icons/filter';
import FolderIcon from '../../../common/icons/folder';
import { PROJECT_STATE_LABEL } from '../../../core/constants/domain';
import Gate from '../../../global/contexts/auth/gate';
import { nameOf } from '../../../global/contexts/catalogs/catalogs';
import { BOARD_PARAMS, countActiveFilters } from '../utils/board';

import '../styles/board-toolbar.css';

function Select({ label, value, onChange, children, hint }) {
    const id = useId();
    return (
        <div className="board-toolbar__field">
            <label htmlFor={id} className="board-toolbar__label">{label}</label>
            <select id={id} className="board-toolbar__select" value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
                {children}
            </select>
            {hint && <p className="board-toolbar__hint">{hint}</p>}
        </div>
    );
}

export default function BoardToolbar({ filters, projects, repositories, tiers, onChange, onClear, children }) {
    const [openSheet, setOpenSheet] = useState(null);
    const close = () => setOpenSheet(null);
    const activeCount = countActiveFilters(filters);

    const projectOptions = [
        { value: '', label: 'Todos los proyectos', icon: <FolderIcon size={18} /> },
        ...[...(projects?.values() ?? [])].map((project) => ({
            value: project.id,
            label: project.name,
            description: PROJECT_STATE_LABEL[project.state] ?? undefined,
            icon: <FolderIcon size={18} />,
        })),
    ];
    const projectRepositories = [...(repositories?.values() ?? [])]
        .filter((repo) => !filters.projectId || repo.projectId === filters.projectId);
    const projectTiers = [...(tiers?.values() ?? [])].filter((tier) => tier.projectId === filters.projectId);
    const projectLabel = filters.projectId
        ? `Proyecto: ${nameOf(projects, filters.projectId, 'Proyecto')}. Cambiar`
        : 'Elegir proyecto';

    return (
        <div className="board-toolbar">
            <IconButton
                icon={<FolderIcon size={20} />}
                label={projectLabel}
                badge={filters.projectId ? true : undefined}
                isActive={!!filters.projectId}
                aria-haspopup="dialog"
                onClick={() => setOpenSheet('project')}
            />
            <IconButton
                icon={<FilterIcon size={20} />}
                label={activeCount ? `Filtros (${activeCount} activos)` : 'Filtros'}
                badge={activeCount}
                isActive={activeCount > 0}
                aria-haspopup="dialog"
                onClick={() => setOpenSheet('filters')}
            />
            {children}

            <PickerSheet
                isOpen={openSheet === 'project'}
                title="Proyecto"
                options={projectOptions}
                value={filters.projectId ?? ''}
                onSelect={(value) => onChange(BOARD_PARAMS.PROJECT, value)}
                onClose={close}
            />

            <Sheet
                isOpen={openSheet === 'filters'}
                title="Filtros del tablero"
                description="Los mismos filtros que el tablero del panel de escritorio."
                onClose={close}
                footer={(
                    <>
                        <Button variant="ghost" onClick={onClear}>Limpiar</Button>
                        <Button variant="primary" onClick={close}>Ver tablero</Button>
                    </>
                )}
            >
                <Select
                    label="Repositorio"
                    value={filters.repositoryId}
                    onChange={(value) => onChange(BOARD_PARAMS.REPOSITORY, value)}
                    hint={filters.projectId ? undefined : 'Elige un proyecto para ver solo sus repositorios.'}
                >
                    <option value="">Todos los repositorios</option>
                    {projectRepositories.map((repo) => <option key={repo.id} value={repo.id}>{repo.name}</option>)}
                </Select>
                <Gate domain="tiers:read">
                    {filters.projectId ? (
                        <Select label="Tier" value={filters.tierId} onChange={(value) => onChange(BOARD_PARAMS.TIER, value)}>
                            <option value="">Todos los tiers</option>
                            {projectTiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}
                        </Select>
                    ) : (
                        <p className="board-toolbar__hint">El filtro de tier aparece al elegir un proyecto.</p>
                    )}
                </Gate>
                <Toggle
                    label="Mostrar hallazgos"
                    description="Tareas abiertas desde otra tarea. Ocultas por defecto."
                    checked={filters.showFindings}
                    onChange={(isOn) => onChange(BOARD_PARAMS.FINDINGS, isOn ? 'mostrar' : '')}
                />
            </Sheet>
        </div>
    );
}
